import React, { useMemo } from 'react';
import KoobInputLabel from './KoobInputLabel';
import KoobInputSelect from './KoobInputSelect';

export interface KoobSelectOption {
  label: string;
  value: string;
}

export interface KoobSelectCurrencyProps {
  currencies?: Array<KoobSelectOption>;
  value?: string | KoobSelectOption | null;
  onChange?: (value: KoobSelectOption | string) => void;
  label?: string;
  required?: boolean;
  defaultCurrency?: string | KoobSelectOption;
  disabled?: boolean;
  loading?: boolean;
  tooltipDescription?: string;
  hasError?: boolean;
}

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '元',
  SEK: 'kr',
  NZD: 'NZ$',
};

const getSymbolFromCurrency = (currencyCode: string): string => {
  const code = currencyCode.toUpperCase();
  if (currencySymbols[code]) {
    return currencySymbols[code];
  }
  try {
    const formatted = (0).toLocaleString('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatted.replace(/[0-9\s.,-]/g, '').trim();
  } catch (e) {
    return '';
  }
};

const KoobSelectCurrency: React.FC<KoobSelectCurrencyProps> = ({
  currencies = [],
  value,
  onChange,
  label,
  required,
  defaultCurrency,
  disabled = false,
  loading = false,
  tooltipDescription,
  hasError = false,
}) => {
  const options = useMemo<KoobSelectOption[]>(() => {
    if (!currencies.length) return [];

    return currencies
      .map((item) => {
        const code = item.value;
        const symbol = getSymbolFromCurrency(code);

        // Format with a symbol if the starting label has not already been customized
        const formattedLabel =
          item.label.toUpperCase() === code.toUpperCase() && symbol
            ? `${code.toUpperCase()}  ${symbol}`
            : item.label;

        return {
          ...item,
          label: formattedLabel,
        };
      })
      .sort((c1, c2) => c1.label.localeCompare(c2.label));
  }, [currencies]);

  // Extracting the Selected Value
  const currentValue = typeof value === 'object' ? value?.value : value;
  const defaultOptionVal =
    typeof defaultCurrency === 'object'
      ? defaultCurrency?.value
      : defaultCurrency;

  const handleSelect = (selectedValue: string) => {
    if (!onChange) return;
    const option = options.find((c) => c.value === selectedValue);
    onChange(option ?? selectedValue);
  };

  return (
    <KoobInputLabel
      label={label}
      required={required}
      tooltipDescription={tooltipDescription}
    >
      <KoobInputSelect
        options={options}
        loading={loading}
        value={currentValue ?? defaultOptionVal ?? ''}
        onValueChange={(val: string) => handleSelect(val)}
        disabled={disabled}
        error={hasError}
        searchable
        clearable
      />
    </KoobInputLabel>
  );
};

export default KoobSelectCurrency;
